import { CommonModule } from '@angular/common';
import { Component, inject } from '@angular/core';
import {
  FormBuilder,
  FormControl,
  type FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';

import type { TermAndCondition } from '@core/models/app.model';
import type { ControlsOf } from '@core/models/forms.model';
import { NotifyService } from '@core/services/notifyService.service';
import { MAX_UPLOAD_FILE_SIZE } from '@shared/constants/ume-constants';

import type { IUploadTermData } from '../../models/services.model';

const FIELDS = {
  NAME: 'termName',
  RELEVANCE: 'relevance',
  IS_GLOBAL: 'isGlobal',
} as const;

interface TermForm {
  [FIELDS.NAME]: string | null;
  [FIELDS.RELEVANCE]: number | null;
  [FIELDS.IS_GLOBAL]: boolean | null;
}

@Component({
  selector: 'ume-upload-term-dialog',
  templateUrl: './upload-term-dialog.component.html',
  styleUrls: ['./upload-term-dialog.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatInputModule,
    MatFormFieldModule,
    MatProgressSpinnerModule,
    MatSlideToggleModule,
  ],
})
export class UploadTermDialogComponent {
  readonly fields = FIELDS;

  public notify: NotifyService = inject(NotifyService);
  public data: {
    serviceOfferingId: string;
    term: TermAndCondition;
  } = inject(MAT_DIALOG_DATA);
  private fb: FormBuilder = inject(FormBuilder);
  private dialogRef: MatDialogRef<UploadTermDialogComponent> = inject(
    MatDialogRef<UploadTermDialogComponent>
  );

  public termForm: FormGroup;
  public serviceOfferingId!: string;
  public currentTerm!: TermAndCondition;
  public title = 'Upload new term';

  public file: File | null = null;
  public fileSelected = false;

  constructor() {
    if (this.data.term) {
      this.currentTerm = this.data.term;
      this.fileSelected = !!this.data.term.file;
      this.title = 'Update term';
    }
    this.serviceOfferingId = this.data.serviceOfferingId;

    this.termForm = this.fb.group<ControlsOf<TermForm>>({
      [FIELDS.NAME]: new FormControl(this.data.term ? this.data.term.name : null, {
        validators: [Validators.required],
      }),
      [FIELDS.RELEVANCE]: new FormControl(
        this.data.term ? (this.data.term.relevance ?? null) : null,
        {
          validators: [Validators.required],
        }
      ),
      [FIELDS.IS_GLOBAL]: new FormControl(
        this.data.term ? (this.data.term.isGlobal ?? false) : false
      ),
    });
  }

  public onFileSelected(event: unknown | any): void {
    if (event.target.files[0].type !== 'application/pdf') {
      this.notify.notifyErrorOnly('For security reasons, we only accept files in PDF format.');
      return;
    }

    if (event.target.files[0].size > MAX_UPLOAD_FILE_SIZE) {
      this.notify.notifyErrorOnly(
        'For security reasons, we allow uploading files no larger than 30Mb.'
      );
      return;
    }

    this.file = event.target.files[0];
    this.fileSelected = true;
  }

  public save(): void {
    const uploadData: IUploadTermData = {
      serviceOfferingId: this.serviceOfferingId,
      termsAndConditions: {
        guid: this.currentTerm?.guid || '',
        name: this.termForm.value.termName,
        relevance: this.termForm.value.relevance,
        isGlobal: this.termForm.value.isGlobal,
        publishedTimestamp: Date.now().toString(),
      },
    };
    if (this.file) {
      this.dialogRef.close({ uploadData, file: this.file });
    } else {
      this.dialogRef.close({ uploadData, file: null });
    }
  }

  public removeFile(): void {
    this.file = null;
    this.fileSelected = false;
  }
}
